import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';

const joinURL = (base, path) =>
    `${(base || '').replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;

async function fetchContactInquiries() {
    const url = joinURL(process.env.REACT_APP_API_URL, '/contact-inquiry');
    const res = await axios.get(url, { withCredentials: true });
    return Array.isArray(res?.data?.data) ? res.data.data : [];
}

export default function ContactInquiry() {
    const { data = [], isLoading, isError, error, isFetching, refetch } = useQuery({
        queryKey: ['contact-inquiry'],
        queryFn: fetchContactInquiries,
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        keepPreviousData: true,
    });

    // Client-side pagination state
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

    // Prevent blank page if user is on a high page and data shrinks
    useEffect(() => {
        const total = data.length;
        const start = pagination.pageIndex * pagination.pageSize;
        if (total > 0 && start >= total) {
            const last = Math.max(0, Math.ceil(total / pagination.pageSize) - 1);
            if (last !== pagination.pageIndex) {
                setPagination((p) => ({ ...p, pageIndex: last }));
            }
        }
    }, [data, pagination.pageIndex, pagination.pageSize]);

    const columns = [
        { accessorKey: 'id', header: 'ID', size: 70 },
        { accessorKey: 'firstName', header: 'First Name' },
        { accessorKey: 'lastName', header: 'Last Name' },
        { accessorKey: 'email', header: 'Email' },
        { accessorKey: 'phone', header: 'Phone' },
        { accessorKey: 'organization', header: 'Organization' },
        { accessorKey: 'subject', header: 'Subject' },
        {
            accessorKey: 'message',
            header: 'Message',
            size: 300,
            Cell: ({ cell }) => <div style={{ whiteSpace: 'pre-wrap' }}>{cell.getValue()}</div>,
        },
        {
            accessorKey: 'createdAt',
            header: 'Created At',
            Cell: ({ cell }) => {
                const v = cell.getValue();
                try {
                    return v ? new Date(v).toLocaleString() : '';
                } catch {
                    return v ?? '';
                }
            },
        },
        { accessorKey: 'country', header: 'Country' },
        { accessorKey: 'jobRole', header: 'Job Role' },
        { accessorKey: 'userAgent', header: 'User Agent' },
    ];

    const table = useMaterialReactTable({
        columns,
        data: data.map((r, i) => ({
            ...r,
            __rowId: r?.id,
        })),
        getRowId: (row) => row.__rowId,
        // Enable client-side pagination
        enablePagination: true,
        state: {
            isLoading,
            showAlertBanner: isError,
            showProgressBars: isLoading || isFetching,
            pagination,
        },
        onPaginationChange: setPagination,
        initialState: {
            density: 'compact',
            pagination: { pageIndex: 0, pageSize: 10 },
            columnVisibility: { userAgent: false },
        },
        autoResetPageIndex: false, // keep current page unless out-of-range (handled above)
        enableColumnResizing: true,
        enableStickyHeader: true,
        muiToolbarAlertBannerProps: isError
            ? { color: 'error', children: error?.message || 'Failed to load data' }
            : undefined,
    });

    return (
        <div className="tw-flex-1 tw-flex tw-flex-col tw-items-stretch tw-p-7">
            <MaterialReactTable table={table} />
        </div>
    );
}
